/**
 * 404 — reached when a deep link or a stale route points at nothing.
 *
 * It renders outside the tab tree, which is exactly why it was the one screen
 * still painting untokenised: no ScreenContainer, so no background at all, and
 * an uncoloured title. In dark mode that meant near-black text on a
 * transparent backdrop — the screen a user hits when something is already
 * broken was itself unreadable.
 */
import { Stack, useRouter } from 'expo-router'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { SearchX } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { Button } from '@/components/ui/Button'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, TEXT_STYLES } from '@/lib/theme/tokens'
import { FONTS, SPACING } from '@/lib/brand'

export default function NotFoundScreen() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const router = useRouter()

  return (
    <>
      <Stack.Screen options={{ title: t('errors.not_found') }} />
      <ScreenContainer scroll={false}>
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING.lg,
            gap: SPACING.md,
          }}
        >
          <SearchX size={48} color={colors.textMuted} strokeWidth={1.5} />
          <Text
            style={{
              ...TEXT_STYLES.h3,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
              textAlign: 'center',
              // Wide enough that the sentence doesn't strand its last word on
              // a line of its own.
              maxWidth: 320,
            }}
          >
            {t('errors.not_found')}
          </Text>

          {/* A real primary action, not a bordered text link: this is the only
              way out of the screen when the user arrived by deep link. */}
          <Button
            title={t('empty.no_bookings_action')}
            onPress={() => router.replace('/')}
            variant="primary"
            size="md"
            accessibilityLabel={t('empty.no_bookings_action')}
            testID="not-found.back"
          />
        </View>
      </ScreenContainer>
    </>
  )
}
